import { useEffect, useRef, useState } from 'react';
import { Provider } from 'react-redux';
import { createRoot } from 'react-dom/client';
import createCache from '@emotion/cache';
import { CacheProvider } from '@emotion/react';

const ShadowWrapper = ({ component: Component, fieldId, store,...props }) => {
  const hostRef = useRef(null);
  const rootRef = useRef(null);
  const shadowRootRef = useRef(null);
  const [isStoreValid, setIsStoreValid] = useState(false);

  useEffect(() => {
    // Validate store before proceeding
    if (!store || typeof store.getState !== 'function') {
      console.warn('Store not valid yet, skipping shadow DOM creation');
      setIsStoreValid(false);
      return;
    }
    
    setIsStoreValid(true);
    
    const host = hostRef.current;
    if (!host) return;

    try {
      if (!host.shadowRoot) {
        host.attachShadow({ mode: 'open' });
      }

      const shadowRoot = host.shadowRoot;
      shadowRootRef.current = shadowRoot;

      const mountNode = document.createElement('div');
      shadowRoot.appendChild(mountNode);

      const shadowCache = createCache({
        key: 'mui-shadow',
        container: shadowRoot,
      });

      const root = createRoot(mountNode);
      rootRef.current = root;

      root.render(
        <CacheProvider value={shadowCache}>
          <Provider store={store}>
            <Component fieldId={fieldId} {...props} shadowRoot={shadowRoot} />
          </Provider>
        </CacheProvider>
      );

      return () => {
        setTimeout(() => {
          if (rootRef.current) {
            rootRef.current.unmount();
          }
          if (mountNode && mountNode.parentNode) {
            mountNode.parentNode.removeChild(mountNode);
          }
        }, 0);
      };
    } catch (error) {
      console.error('Error creating shadow DOM:', error);
    }
  }, [fieldId, store, isStoreValid]);

  // Only render the host div if store is valid
  return isStoreValid ? <div ref={hostRef} /> : null;
};

export default ShadowWrapper;